import { z } from 'zod';

// In-person events (/api/events): the event, its audiences and invitees, messages, documents,
// check-ins, Eventbrite and PheedLoop, insights, the past-events seeds, and the dashboard
// (follow-ups, notes, ledger). Schemas check shape only: days, settings, needs, audiences and
// outreach details are cleaned by lib/eventRules.ts and lib/eventAudiences.ts, which keep their
// messages. The lists below mirror the backend (the tests compare them).

const UUID = /^[0-9a-f-]{36}$/i;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

const Id = z.string().regex(UUID, 'is not an id');
const Day = z.string().regex(DAY, 'is a date (YYYY-MM-DD)');
const Str = z.string().nullish();
const Flag = z.boolean().optional();
/** Cleaned further by the service (rules.ts / eventAudiences.ts). */
const Free = z.unknown().optional();
/** A list of ids, or a comma-separated string of them. */
const IdList = z.union([z.array(z.string()), z.string()]).optional();

export const EventInviteeStatus = z.enum([
  'invited',
  'registered',
  'confirmed',
  'declined',
  'waitlisted',
  'attended',
  'no_show',
]);
export type EventInviteeStatus = z.infer<typeof EventInviteeStatus>;

export const EventAudienceSourceKind = z.enum(['groups', 'filters', 'list', 'public']);
export type EventAudienceSourceKind = z.infer<typeof EventAudienceSourceKind>;

export const EventMessageKind = z.enum(['invitation', 'confirmation', 'reminder', 'evaluation']);
export type EventMessageKind = z.infer<typeof EventMessageKind>;

export const EventDocumentKind = z.enum(['hotel', 'catering', 'dinner', 'nametags', 'checkin']);
export type EventDocumentKind = z.infer<typeof EventDocumentKind>;

export const EventFollowupState = z.enum(['open', 'done', 'dismissed']);
export type EventFollowupState = z.infer<typeof EventFollowupState>;

/** How a check-in was recorded (event_checkins.via). */
export const EventCheckinVia = z.enum(['link', 'operator', 'import']);
export type EventCheckinVia = z.infer<typeof EventCheckinVia>;

// ---------------------------------------------------------------- params

export const EventCoreParams = z.strictObject({ id: Id });
export type EventCoreParams = z.input<typeof EventCoreParams>;

export const EventInviteeParams = z.strictObject({ inviteeId: Id });
export type EventInviteeParams = z.input<typeof EventInviteeParams>;

export const EventAudienceParams = z.strictObject({ id: Id, key: z.string().min(1).max(100) });
export type EventAudienceParams = z.input<typeof EventAudienceParams>;

export const EventDocumentParams = z.strictObject({ id: Id, kind: EventDocumentKind });
export type EventDocumentParams = z.input<typeof EventDocumentParams>;

export const EventCheckinLinkParams = z.strictObject({ linkId: Id });
export type EventCheckinLinkParams = z.input<typeof EventCheckinLinkParams>;

// ---------------------------------------------------------------- queries

/** GET /api/events/insights */
export const EventInsightsQuery = z.strictObject({
  from: z.string().optional(),
  to: z.string().optional(),
  kind: z.string().optional(),
});
export type EventInsightsQuery = z.input<typeof EventInsightsQuery>;

/** GET /api/events/website-candidates */
export const EventWebsiteCandidatesQuery = z.strictObject({ since: z.union([Day, z.literal('')]).optional() });
export type EventWebsiteCandidatesQuery = z.input<typeof EventWebsiteCandidatesQuery>;

/** GET /api/events/person; an id that is not an id finds nothing. */
export const EventPersonQuery = z.strictObject({
  userId: z.string().optional(),
  contactId: z.string().optional(),
});
export type EventPersonQuery = z.input<typeof EventPersonQuery>;

/** GET /api/events/:id/invitees; unknown statuses in the list are dropped by the service. */
export const EventInviteesQuery = z.strictObject({
  status: z.string().optional(),
  audience: z.string().optional(),
  q: z.string().max(200).optional(),
  flagged: z.string().optional(),
});
export type EventInviteesQuery = z.input<typeof EventInviteesQuery>;

/** DELETE /api/events/:id/audiences/:key */
export const EventAudienceDeleteQuery = z.strictObject({ moveTo: z.string().optional() });
export type EventAudienceDeleteQuery = z.input<typeof EventAudienceDeleteQuery>;

/** GET /api/events/:id/notes?day=YYYY-MM-DD|next */
export const EventNotesQuery = z.strictObject({ day: z.union([Day, z.literal('next'), z.literal('')]).optional() });
export type EventNotesQuery = z.input<typeof EventNotesQuery>;

// ---------------------------------------------------------------- bodies

const eventShape = {
  title: Str,
  titleFr: Str,
  kind: Str,
  mode: Str,
  city: Str,
  venue: Str,
  notes: Str,
  formLink: Str,
  evaluationUrl: Str,
  contactEmail: Str,
  parentEventId: Str,
  days: Free,
  settings: Free,
  audiences: Free,
  outreach: Free,
  counts: Free,
  composition: Free,
};

/** POST /api/events; eventId turns an existing events row into an in-person event. */
export const EventCreateRequest = z.strictObject({ ...eventShape, eventId: Str });
export type EventCreateRequest = z.input<typeof EventCreateRequest>;

/** PATCH /api/events/:id; only the fields sent change. */
export const EventUpdateRequest = z.strictObject(eventShape);
export type EventUpdateRequest = z.input<typeof EventUpdateRequest>;

/** POST /api/events/seed-past; rows carry each source's own columns, checked by the seed. */
export const EventSeedPastRequest = z.strictObject({
  manifestKey: z.string(),
  rows: z.array(z.record(z.string(), z.unknown())).optional(),
  dryRun: Flag,
});
export type EventSeedPastRequest = z.input<typeof EventSeedPastRequest>;

/** POST /api/events/seed-outreach */
export const EventSeedOutreachRequest = z.strictObject({ dryRun: Flag });
export type EventSeedOutreachRequest = z.input<typeof EventSeedOutreachRequest>;

/** POST /api/events/:id/clone */
export const EventCloneRequest = z.strictObject({
  title: Str,
  titleFr: Str,
  startDate: Str,
  withSpeakers: Flag,
});
export type EventCloneRequest = z.input<typeof EventCloneRequest>;

/** One person to invite; resolved (and refused one by one) by the service. */
const InviteePerson = z.strictObject({
  userId: Str,
  contactId: Str,
  email: Str,
  newContact: z.record(z.string(), z.unknown()).nullish(),
});

/** POST /api/events/:id/invitees */
export const EventAddInviteesRequest = z.strictObject({
  people: z.array(InviteePerson),
  audienceKey: Str,
  role: Str,
});
export type EventAddInviteesRequest = z.input<typeof EventAddInviteesRequest>;

const audienceShape = {
  name: Str,
  source: z
    .looseObject({ kind: EventAudienceSourceKind.optional(), core: Free, groupIds: Free, filters: Free })
    .nullish(),
  days: z.array(z.string()).nullish(),
  funded: z.boolean().nullish(),
  opensOn: Str,
  invitedOutside: Free,
};

/** POST /api/events/:id/audiences */
export const EventAudienceCreateRequest = z.strictObject(audienceShape);
export type EventAudienceCreateRequest = z.input<typeof EventAudienceCreateRequest>;

/** PATCH /api/events/:id/audiences/:key; only the fields sent change. */
export const EventAudienceUpdateRequest = z.strictObject(audienceShape);
export type EventAudienceUpdateRequest = z.input<typeof EventAudienceUpdateRequest>;

/** PUT /api/events/:id/audiences/order */
export const EventAudienceOrderRequest = z.strictObject({ keys: z.array(z.string()) });
export type EventAudienceOrderRequest = z.input<typeof EventAudienceOrderRequest>;

/** DELETE /api/events/:id/audiences/:key (moveTo also accepted as a query value) */
export const EventAudienceDeleteRequest = z.strictObject({ moveTo: Str });
export type EventAudienceDeleteRequest = z.input<typeof EventAudienceDeleteRequest>;

/** POST /api/events/:id/audiences/:key/build */
export const EventAudienceBuildRequest = z.strictObject({ filters: Free, dryRun: Flag });
export type EventAudienceBuildRequest = z.input<typeof EventAudienceBuildRequest>;

/** POST /api/events/:id/invitees/status; ids that are not this event's invitees are skipped. */
export const EventInviteeStatusRequest = z.strictObject({
  inviteeIds: z.array(z.string()).optional(),
  status: EventInviteeStatus,
});
export type EventInviteeStatusRequest = z.input<typeof EventInviteeStatusRequest>;

/** PATCH /api/events/invitees/:inviteeId; needs are cleaned by rules.cleanNeeds. */
export const EventInviteeUpdateRequest = z.strictObject({
  audienceKey: Str,
  role: Str,
  note: Str,
  needs: Free,
  status: EventInviteeStatus.optional(),
  statusNote: Str,
});
export type EventInviteeUpdateRequest = z.input<typeof EventInviteeUpdateRequest>;

const messageShape = {
  kind: EventMessageKind,
  audience: Str,
  inviteeIds: z.array(z.string()).nullish(),
  vars: z.record(z.string(), z.unknown()).nullish(),
};

/** POST /api/events/:id/messages/preview */
export const EventMessagePreviewRequest = z.strictObject(messageShape);
export type EventMessagePreviewRequest = z.input<typeof EventMessagePreviewRequest>;

/** POST /api/events/:id/messages/send; the service refuses anything but confirm: true. */
export const EventMessageSendRequest = z.strictObject({ ...messageShape, confirm: z.boolean().optional() });
export type EventMessageSendRequest = z.input<typeof EventMessageSendRequest>;

/** POST /api/events/:id/checkin-links */
export const EventCheckinLinkRequest = z.strictObject({ label: Str });
export type EventCheckinLinkRequest = z.input<typeof EventCheckinLinkRequest>;

/** POST /api/events/:id/checkins; the day must be one of the event's (checked by the service). */
export const EventOperatorCheckinRequest = z.strictObject({
  inviteeIds: z.array(z.string()).optional(),
  day: Str,
  undo: Flag,
});
export type EventOperatorCheckinRequest = z.input<typeof EventOperatorCheckinRequest>;

/** POST /api/events/:id/unlock: a super admin reopens a locked event for a few days. */
export const EventUnlockRequest = z.strictObject({ reason: z.string().trim().min(5).max(500) });
export type EventUnlockRequest = z.input<typeof EventUnlockRequest>;

/** POST /api/events/:id/close */
export const EventCloseRequest = z.strictObject({ dryRun: Flag });
export type EventCloseRequest = z.input<typeof EventCloseRequest>;

/** POST /api/events/:id/gift-cards */
export const EventGiftCardsRequest = z.strictObject({
  inviteeIds: IdList,
  amountCents: z.union([z.number().int().min(0), z.string(), z.null()]).optional(),
  note: Str,
});
export type EventGiftCardsRequest = z.input<typeof EventGiftCardsRequest>;

/** An uploaded file: CSV text or base64 .xlsx; size and format are checked by the importer. */
const uploadShape = { fileName: Str, content: z.string().nullish(), confirm: Flag };

/** POST /api/events/:id/zoom */
export const EventZoomUploadRequest = z.strictObject(uploadShape);
export type EventZoomUploadRequest = z.input<typeof EventZoomUploadRequest>;

/** POST /api/events/:id/eventbrite */
export const EventEventbriteUploadRequest = z.strictObject({ ...uploadShape, sheet: Str });
export type EventEventbriteUploadRequest = z.input<typeof EventEventbriteUploadRequest>;

/** PUT /api/events/:id/eventbrite/link; null or '' unlinks, the id format is checked by the service. */
export const EventEventbriteLinkRequest = z.strictObject({
  eventbriteEventId: z.union([z.string(), z.number(), z.null()]).optional(),
});
export type EventEventbriteLinkRequest = z.input<typeof EventEventbriteLinkRequest>;

/** POST /api/events/:id/eventbrite/sync */
export const EventEventbriteSyncRequest = z.strictObject({ dryRun: Flag, full: Flag });
export type EventEventbriteSyncRequest = z.input<typeof EventEventbriteSyncRequest>;

/** POST /api/events/:id/pheedloop/file */
export const EventPheedloopFileRequest = z.strictObject({ all: Flag });
export type EventPheedloopFileRequest = z.input<typeof EventPheedloopFileRequest>;

/** POST /api/events/:id/pheedloop/added */
export const EventPheedloopAddedRequest = z.strictObject({ inviteeIds: IdList });
export type EventPheedloopAddedRequest = z.input<typeof EventPheedloopAddedRequest>;

/** POST /api/events/:id/pheedloop/checkins */
export const EventPheedloopCheckinsRequest = z.strictObject({ ...uploadShape, day: Str });
export type EventPheedloopCheckinsRequest = z.input<typeof EventPheedloopCheckinsRequest>;

// ---------------------------------------------------------------- dashboard

/** POST /api/events/:id/followups/state; the note guard (banking, cards, SIN) stays in the service. */
export const EventFollowupStateRequest = z.strictObject({
  key: z.string(),
  state: EventFollowupState,
  note: Str,
});
export type EventFollowupStateRequest = z.input<typeof EventFollowupStateRequest>;

/** POST /api/events/:id/notes; no day: a note for next year. */
export const EventNoteRequest = z.strictObject({ day: z.union([Day, z.literal('')]).nullish(), text: Str });
export type EventNoteRequest = z.input<typeof EventNoteRequest>;
